import { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Save, RotateCcw, ToggleLeft, ToggleRight, Pencil, AlertCircle } from 'lucide-react';
import { useEmailTemplates, useEmailEvents, useUpdateEmailTemplate, useResetEmailTemplate } from '@/hooks/useEmailTemplates';
import { Button, Card, Input, Modal, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import Editor from '@monaco-editor/react';
import mjml2html from 'mjml-browser';

function isMjml(source: string): boolean {
  return source.trimStart().startsWith('<mjml');
}

function compileSource(source: string): { html: string; error: string | null } {
  if (!isMjml(source)) {
    // Raw HTML — pass through directly
    return { html: source, error: null };
  }
  try {
    const result = mjml2html(source, { validationLevel: 'soft' });
    const warnings = result.errors?.length
      ? result.errors.map((e: any) => e.formattedMessage || e.message).join('; ')
      : null;
    return { html: result.html, error: warnings };
  } catch (err: any) {
    return { html: '', error: err.message || 'MJML compilation failed' };
  }
}

export default function EmailTemplateSettings() {
  const { data: templates, isLoading: templatesLoading } = useEmailTemplates();
  const { data: events, isLoading: eventsLoading } = useEmailEvents();
  const updateTemplate = useUpdateEmailTemplate();
  const resetTemplate = useResetEmailTemplate();

  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [mjmlSource, setMjmlSource] = useState('');
  const [compiledHtml, setCompiledHtml] = useState('');
  const [mjmlError, setMjmlError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentTemplate = templates?.find((t) => t.event === editingEvent);
  const currentEvent = events?.find((e) => e.event === editingEvent);

  const variableList = currentEvent?.variables ?? [];

  // Load template data when opening editor
  useEffect(() => {
    if (currentTemplate && editingEvent) {
      setSubject(currentTemplate.subject);
      setReplyTo(currentTemplate.replyTo || '');
      setIsActive(currentTemplate.isActive);
      setHasChanges(false);
      setMjmlError(null);

      // Load MJML source from designJson, or fall back to raw HTML body
      const source = currentTemplate.designJson?.mjml || currentTemplate.body || '';
      setMjmlSource(source);

      // Initial compilation
      const result = compileSource(source);
      setCompiledHtml(result.html);
      if (result.error && !result.html) {
        setMjmlError(result.error);
      }
    }
  }, [currentTemplate?.id, editingEvent]);

  const handleMjmlChange = useCallback((value: string | undefined) => {
    const source = value ?? '';
    setMjmlSource(source);
    setHasChanges(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const result = compileSource(source);
      if (result.html) {
        setCompiledHtml(result.html);
      }
      setMjmlError(result.error);
    }, 400);
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const insertVariable = (variable: string, target: 'subject' | 'replyTo') => {
    const tag = `{{${variable}}}`;
    if (target === 'subject') {
      setSubject((prev) => prev + tag);
    } else {
      setReplyTo((prev) => prev + tag);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!editingEvent) return;
    setSaving(true);
    try {
      const mjml = isMjml(mjmlSource);
      let body: string;
      let designJson: any;

      if (mjml) {
        // MJML: compile to HTML, store source for round-tripping
        const result = compileSource(mjmlSource);
        body = result.html || compiledHtml;
        designJson = { mjml: mjmlSource };
      } else {
        // Raw HTML: save directly
        body = mjmlSource;
        designJson = null;
      }

      await updateTemplate.mutateAsync({
        event: editingEvent,
        data: {
          subject,
          body,
          designJson,
          isActive,
          replyTo: replyTo || undefined,
        },
      });
      setHasChanges(false);
      setEditingEvent(null);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editingEvent) return;
    if (!confirm('Reset this template to the default content? Your customizations will be lost.')) return;
    await resetTemplate.mutateAsync(editingEvent);
  };

  const handleClose = () => {
    if (hasChanges && !confirm('You have unsaved changes. Discard them?')) return;
    setEditingEvent(null);
  };

  const isLoading = templatesLoading || eventsLoading;

  if (isLoading) {
    return (
      <Card padding="none">
        <div className="p-6 flex items-center gap-2">
          <Mail className="w-5 h-5 text-grey-400" />
          <h2 className="text-heading-3 text-secondary">Email Templates</h2>
        </div>
        <div className="px-6 pb-6">
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </Card>
    );
  }

  // Build list: merge events with their template (if exists) for status
  const templateRows = (events ?? []).map((evt) => {
    const tmpl = templates?.find((t) => t.event === evt.event);
    return { event: evt.event, label: evt.label, description: evt.description, isActive: tmpl?.isActive ?? true };
  });

  return (
    <>
      <Card padding="none">
        <div className="p-6 flex items-center gap-2">
          <Mail className="w-5 h-5 text-grey-400" />
          <h2 className="text-heading-3 text-secondary">Email Templates</h2>
        </div>

        {templateRows.length === 0 ? (
          <div className="px-6 pb-6 text-grey-500">No email templates configured.</div>
        ) : (
          <div className="border-t border-grey-200">
            {templateRows.map((row) => (
              <button
                key={row.event}
                type="button"
                onClick={() => setEditingEvent(row.event)}
                className="w-full flex items-center gap-3 px-6 py-3 hover:bg-grey-50 transition-colors border-b border-grey-100 last:border-b-0 text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-secondary truncate">{row.label}</div>
                  <div className="text-xs text-grey-500 truncate">{row.description}</div>
                </div>
                <Badge variant={row.isActive ? 'success' : 'default'} className="text-xs shrink-0">
                  {row.isActive ? 'Active' : 'Inactive'}
                </Badge>
                <Pencil className="w-4 h-4 text-grey-400 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Full-screen editor modal */}
      <Modal
        open={editingEvent !== null}
        onOpenChange={(open) => { if (!open) handleClose(); }}
        title={currentEvent?.label ?? 'Edit Template'}
        description={currentEvent?.description}
        // The editor + preview must fill the dialog: make the Modal's body
        // slot (its last child div) a growing flex column.
        className="max-w-7xl h-[90vh] max-h-[90vh] flex flex-col overflow-y-hidden [&>div:last-child]:flex [&>div:last-child]:flex-1 [&>div:last-child]:min-h-0 [&>div:last-child]:flex-col"
      >
        {/* Action bar */}
        <div className="flex items-center justify-between pb-4 border-b border-grey-200 shrink-0">
          <button
            type="button"
            onClick={() => { setIsActive(!isActive); setHasChanges(true); }}
            className="flex items-center gap-2 text-sm"
          >
            {isActive ? (
              <ToggleRight className="w-6 h-6 text-green-500" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-grey-400" />
            )}
            <span className={isActive ? 'text-green-700 font-medium' : 'text-grey-500'}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={resetTemplate.isPending}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset to Default
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges}
              loading={saving}
            >
              {!saving && <Save className="w-4 h-4 mr-1" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Subject & Reply-To */}
        <div className="space-y-4 pt-4 shrink-0">
          {/* Subject */}
          <div>
            <Input
              label="Subject"
              id="template-subject"
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setHasChanges(true); }}
              placeholder="Email subject line..."
            />
            <div className="flex flex-wrap gap-1 mt-2">
              {variableList.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v, 'subject')}
                  className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Reply-To */}
          <div>
            <Input
              label="Reply-To"
              id="template-replyto"
              value={replyTo}
              onChange={(e) => { setReplyTo(e.target.value); setHasChanges(true); }}
              placeholder="Reply-to email address..."
            />
          </div>

          {/* Available variables info */}
          <div className="p-3 bg-grey-50 rounded-lg">
            <span className="text-xs font-medium text-grey-600 mr-2">Available variables:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {variableList.map((v) => (
                <Badge key={v} variant="info" className="text-xs">
                  {`{{${v}}}`}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* MJML error banner */}
        {mjmlError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-4 shrink-0">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{mjmlError}</p>
          </div>
        )}

        {/* MJML Editor + Live Preview */}
        <div className="flex-1 grid grid-cols-2 gap-4 mt-4 min-h-0">
          {/* Monaco Editor */}
          <div className="flex flex-col min-h-0">
            <span className="block text-sm font-medium text-grey-700 mb-1">
              {isMjml(mjmlSource) ? 'MJML Source' : 'HTML Source'}
            </span>
            <div className="flex-1 border border-grey-200 rounded-lg overflow-hidden min-h-0">
              <Editor
                height="100%"
                language={isMjml(mjmlSource) ? 'xml' : 'html'}
                theme="vs-light"
                value={mjmlSource}
                onChange={handleMjmlChange}
                options={{
                  wordWrap: 'on',
                  minimap: { enabled: false },
                  fontSize: 12,
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  automaticLayout: true,
                }}
              />
            </div>
          </div>

          {/* Live Preview */}
          <div className="flex flex-col min-h-0">
            <span className="block text-sm font-medium text-grey-700 mb-1">Preview</span>
            <div className="flex-1 border border-grey-200 rounded-lg overflow-hidden min-h-0">
              <iframe
                srcDoc={compiledHtml}
                title="Email preview"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
